import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Sample from '../../../types/sample/Sample';
import SampleWriteDTO from '../../../types/sample/SampleWriteDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class SampleService {

    private RESOURCE: string = 'sample';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches the samples from the backend.
     *
     * @param search The search query to filter the samples by.
     * @param page The page number to fetch.
     * @param size The number of elements to fetch per page.
     * @param excludeVehicleModelIds The IDs of the vehicle models to exclude.
     * @param fieldOfUse The field of use to filter the samples by.
     * @param sampleType The sample type to filter the samples by.
     * @param sampleState The sample state to filter the samples by.
     * @param sampleCategory The sample category to filter the samples by.
     * @param sampleDetails The sample details to filter the samples by.
     * @param sampleFormat The sample format to filter the samples by.
     * @param expirationDateBefore The expiration date to filter the samples by.
     * @param sortField The field to sort the samples by.
     * @param sortDirection The direction to sort the samples in.
     * @return The samples.
     */
    public async getSamples(
        search?: string,
        page: number = 0,
        size: number = 10,
        excludeVehicleModelIds?: number[],
        fieldOfUse?: string[],
        sampleType?: number[],
        sampleState?: string[],
        sampleCategory?: number[],
        sampleDetails?: number[],
        sampleFormat?: string[],
        expirationDateBefore?: string,
        sortField?: string,
        sortDirection?: number
    ): Promise<Page<Sample>> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString());

        if (search) {
            params = params.set('search', search);
        }
        if (excludeVehicleModelIds) {
            params = params.set('excludeVehicleModelIds', excludeVehicleModelIds.join(','));
        }
        if (fieldOfUse) {
            params = params.set('fieldOfUse', fieldOfUse.join(','));
        }
        if (sampleType) {
            params = params.set('sampleType', sampleType.join(','));
        }
        if (sampleState) {
            params = params.set('sampleState', sampleState.join(','));
        }
        if (sampleCategory) {
            params = params.set('sampleCategory', sampleCategory.join(','));
        }
        if (sampleDetails) {
            params = params.set('sampleDetail', sampleDetails.join(','));
        }
        if (sampleFormat) {
            params = params.set('format', sampleFormat.join(','));
        }
        if (expirationDateBefore) {
            params = params.set('expirationDateBefore', expirationDateBefore);
        }
        if (sortField) {
            params = params.set('sortField', sortField);
        }
        if (sortDirection) {
            params = params.set('sortDirection', sortDirection.toString());
        } else if (sortField) {
            params = params.set('sortDirection', '1');
        }

        return await this.baseService.get<Page<Sample>>(this.RESOURCE, params);
    }

    /**
     * Fetches multiple samples from the backend.
     *
     * @param id The ID of the samples to fetch.
     * @return The samples.
     */
    public async getSample(id: number): Promise<Sample> {
        return await this.baseService.get<Sample>(this.RESOURCE + '/' + id);
    }

    public async getAllSamples(): Promise<Sample[]> {
        return await this.baseService.get<Sample[]>(this.RESOURCE + '/all');
    }

    /**
     * Creates a sample in the backend.
     *
     * @param sample The sample to create.
     * @return The created sample.
     */
    public async createSample(sample: SampleWriteDTO): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE, sample);
    }

    /**
     * Updates a sample in the backend.
     *
     * @param sample The sample to update.
     * @return The response from the backend.
     */
    public async updateSample(sample: SampleWriteDTO): Promise<void> {
        return await this.baseService.put<void>(this.RESOURCE + '/' + sample.id, sample);
    }

    /**
     * Deletes a sample in the backend.
     *
     * @param id The ID of the sample to delete.
     */
    public async deleteSample(id: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
    }

    /**
     * Adds samples to a vehicle model.
     *
     * @param vehicleModelId The ID of the vehicle model to add the samples to.
     * @param sampleIds The IDs of the samples to add.
     * @return The response from the backend.
     */
    public async addSamplesToVehicleModel(vehicleModelId: number, sampleIds: number[]): Promise<void> {
        return await this.baseService.post<void>(
            this.RESOURCE + '/add-to-vehicle-model/' + vehicleModelId, sampleIds);
    }

    /**
     * Publishes a sample.
     *
     * @param id The ID of the sample to publish.
     * @param comment The comment to add to the publishing action.
     * @return The response from the backend.
     */
    public async publishSample(id: number, comment?: string): Promise<void> {
        if (comment) {
            return this.baseService.post<void>(this.RESOURCE + '/' + id + '/publish?comment=' + comment, {});
        }
        return this.baseService.post<void>(this.RESOURCE + '/' + id + '/publish', {});
    }

    /**
     * Requests approval for a sample.
     *
     * @param id The ID of the sample to request approval for.
     * @param comment The comment to add to the approval request.
     * @return The response from the backend.
     */
    public async requestApproval(id: number, comment?: string): Promise<void> {
        if (comment) {
            return this.baseService.post<void>(
                this.RESOURCE + '/' + id + '/request-approval?comment=' + comment, {});
        }
        return this.baseService.post<void>(this.RESOURCE + '/' + id + '/request-approval', {});
    }

    /**
     * Request changes for a sample.
     *
     * @param id The ID of the sample to request changes for.
     * @param comment The comment to add to the change request.
     * @return The response from the backend.
     */
    public async requestChanges(id: number, comment: string): Promise<void> {
        if (comment) {
            return this.baseService.post<void>(
                this.RESOURCE + '/' + id + '/request-changes?comment=' + comment, {});
        }
        return this.baseService.post<void>(this.RESOURCE + '/' + id + '/request-changes', {});
    }

    /**
     * Approves a sample.
     *
     * @param id The ID of the sample to approve.
     * @param comment The comment to add to the approval action.
     * @return The response from the backend.
     */
    public async approveSampleByReviewer(id: number, comment?: string): Promise<void> {
        if (comment) {
            return this.baseService.post<void>(
                this.RESOURCE + '/' + id + '/approve-by-reviewer?comment=' + comment, {});
        }
        return this.baseService.post<void>(this.RESOURCE + '/' + id + '/approve-by-reviewer', {});
    }

    /**
     * Moves a sample to in production.
     *
     * @param id The ID of the sample.
     * @param comment The comment to add to the state transition.
     * @return The response from the backend.
     */
    public async setSampleInProduction(id: number, comment?: string): Promise<void> {
        if (comment) {
            return this.baseService.post<void>(
                this.RESOURCE + '/' + id + '/in-production?comment=' + comment, {});
        }
        return this.baseService.post<void>(this.RESOURCE + '/' + id + '/in-production', {});
    }

    /**
     * Approves a sample.
     *
     * @param id The ID of the sample to approve.
     * @param comment The comment to add to the approval action.
     * @return The response from the backend.
     */
    public async approveSampleByProcessOwner(id: number, comment?: string): Promise<void> {
        if (comment) {
            return this.baseService.post<void>(
                this.RESOURCE + '/' + id + '/approve-by-process-owner?comment=' + comment, {});
        }
        return this.baseService.post<void>(this.RESOURCE + '/' + id + '/approve-by-process-owner', {});
    }

    /**
     * Archives a sample.
     *
     * @param id The ID of the sample to archive.
     * @param comment The comment to add to the archive action.
     * @return The response from the backend.
     */
    public async archiveSample(id: number, comment?: string): Promise<void> {
        if (comment) {
            return this.baseService.post<void>(this.RESOURCE + '/' + id + '/archive?comment=' + comment, {});
        }
        return this.baseService.post<void>(this.RESOURCE + '/' + id + '/archive', {});
    }

    /**
     * Requests a master sample.
     *
     * @param id The ID of the sample to request a master sample for.
     * @return The response from the backend.
     */
    public async requestMasterSample(id: number): Promise<void> {
        return this.baseService.post<void>(this.RESOURCE + '/' + id + '/request-master-sample', {});
    }

    /**
     * Toggles the on hold status of a sample.
     *
     * @param id The ID of the sample to toggle the on hold status for.
     * @param comment Optional comment to include
     * @return A Promise resolving when the request completes.
     */
    public async toggleOnHold(id: number, comment?: string): Promise<void> {
        let params = new HttpParams();
        if (comment) {
            params = params.set('comment', comment);
        }

        return this.baseService.post<void>(
            `${ this.RESOURCE }/${ id }/toggle-on-hold`,
            {},
            params
        );
    }
}
